import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Phone, Check } from "lucide-react";
import { toast } from "sonner";
import Seo from "../components/Seo";
import api, { CATEGORY_LABELS, formatPrice } from "../lib/api";

export default function ListingDetail() {
  const { id } = useParams();
  const [listing, setListing] = useState(null);
  const [active, setActive] = useState(0);
  const [notFound, setNotFound] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/listings/${id}`)
      .then((r) => { setListing(r.data); setForm((f) => ({ ...f, message: `Dobrý den, mám zájem o inzerát: ${r.data.title}. ` })); })
      .catch(() => setNotFound(true));
  }, [id]);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/inquiries", { ...form, listing_id: listing.id, listing_title: listing.title });
      toast.success("Poptávka byla odeslána. Brzy se ozveme!");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      toast.error("Nepodařilo se odeslat. Zkuste to prosím znovu.");
    } finally {
      setSending(false);
    }
  };

  if (notFound)
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-6 relative z-10">
        <h1 className="font-display text-5xl">Inzerát nenalezen</h1>
        <Link to="/katalog" className="text-[#ff3b30] font-head uppercase tracking-wider">← Zpět na katalog</Link>
      </div>
    );

  if (!listing)
    return <div className="min-h-screen flex items-center justify-center font-display text-2xl text-white/30 animate-pulse relative z-10">Načítání…</div>;

  const catLabel = CATEGORY_LABELS[listing.category] || "";
  const metaTitle = `${listing.title} – ${formatPrice(listing.price)}`;
  const metaDesc = (listing.description || `${catLabel} ${listing.brand} ${listing.size || ""}`.trim())
    .replace(/\s+/g, " ")
    .slice(0, 160);
  const ogImage = listing.images?.[0];

  return (
    <div className="pt-28 pb-24 relative z-10 min-h-screen">
      <Seo title={metaTitle} description={metaDesc} image={ogImage} type="product" />

      <div className="max-w-[1500px] mx-auto px-5 md:px-10">
        <Link to="/katalog" data-testid="back-to-catalog" className="inline-flex items-center gap-2 font-head uppercase tracking-wider text-sm text-white/60 hover:text-white transition-colors mb-8">
          <ArrowLeft size={16} /> Zpět na katalog
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
          {/* Gallery */}
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }}>
            <div className="relative aspect-[4/3] overflow-hidden border border-white/10 bg-[#0d0d0d]">
              <img src={listing.images?.[active] || listing.images?.[0]} alt={listing.title} className="w-full h-full object-cover" data-testid="detail-main-image" />
              <div className="absolute inset-x-0 top-0 h-20 bg-gradient-to-b from-black via-black/45 to-transparent pointer-events-none" />
              <span className="absolute top-4 left-4 font-head uppercase tracking-[0.15em] text-[10px] font-bold bg-[#ff3b30] text-white px-3 py-1.5">
                {CATEGORY_LABELS[listing.category]}
              </span>
            </div>
            {listing.images?.length > 1 && (
              <div className="grid grid-cols-4 gap-3 mt-3">
                {listing.images.map((img, i) => (
                  <button key={i} onClick={() => setActive(i)} className={`aspect-square overflow-hidden border ${active === i ? "border-[#ff3b30]" : "border-white/10"}`}>
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </motion.div>

          {/* Info */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}>
            <div className="font-head uppercase tracking-[0.25em] text-xs text-[#ff3b30] mb-4">{listing.brand}</div>
            <h1 className="font-display text-4xl md:text-5xl leading-[0.95] mb-6">{listing.title}</h1>
            <div className="flex items-center gap-4 mb-3">
              <span className="font-display text-4xl" data-testid="detail-price">{formatPrice(listing.price)}</span>
              {listing.vat && (
                <span className="font-head uppercase tracking-wider text-[10px] px-2.5 py-1 border border-white/30 text-white/60" data-testid="detail-vat-badge">
                  bez DPH
                </span>
              )}
              <span className={`font-head uppercase tracking-wider text-xs px-3 py-1.5 border ${listing.condition === "nove" ? "border-white text-white" : "border-white/30 text-white/60"}`}>
                {listing.condition === "nove" ? "Nové" : "Použité"}
              </span>
            </div>
            {listing.vat && (
              <div className="space-y-1 mb-8 text-sm" data-testid="detail-vat-breakdown">
                <div className="text-white/60" data-testid="detail-price-with-vat">
                  Cena s DPH: <span className="text-white/90 font-head">{formatPrice(Math.round(listing.price * 1.21))}</span>
                </div>
                <div className="text-white/45" data-testid="detail-vat-amount">
                  z toho DPH (21 %): <span className="text-white/70">{formatPrice(Math.round(listing.price * 1.21) - listing.price)}</span>
                </div>
              </div>
            )}

            <div className="space-y-3 mb-8 border-y border-white/10 py-6">
              <Row label="Kategorie" value={CATEGORY_LABELS[listing.category]} />
              <Row label="Značka" value={listing.brand} />
              {listing.size && <Row label="Rozměr / rozteč" value={listing.size} />}
              <Row label="Stav" value={listing.condition === "nove" ? "Nové" : "Použité"} />
            </div>

            {listing.description && <p className="text-white/65 leading-relaxed mb-8 whitespace-pre-line">{listing.description}</p>}

            <a href="tel:+420777181818" className="inline-flex items-center gap-2 text-white/80 hover:text-[#ff3b30] transition-colors mb-8 font-head">
              <Phone size={16} /> Zavolat: +420 777 181 818
            </a>

            {/* Inquiry form */}
            <div className="border border-white/10 bg-[#0d0d0d] p-6 md:p-8">
              <h3 className="font-head text-xl font-semibold mb-5">Mám zájem — poptávka</h3>
              <form onSubmit={submit} className="space-y-4" data-testid="detail-inquiry-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input required data-testid="inquiry-name" placeholder="Jméno *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#111] border border-[#222] px-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-[#ff3b30]" />
                  <input required type="email" data-testid="inquiry-email" placeholder="E-mail *" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-[#111] border border-[#222] px-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-[#ff3b30]" />
                </div>
                <input data-testid="inquiry-phone" placeholder="Telefon" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full bg-[#111] border border-[#222] px-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-[#ff3b30]" />
                <textarea required rows={4} data-testid="inquiry-message" placeholder="Zpráva *" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full bg-[#111] border border-[#222] px-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-[#ff3b30] resize-none" />
                <button disabled={sending} data-testid="inquiry-submit" className="w-full font-head uppercase tracking-wider bg-[#ff3b30] text-white py-4 hover:bg-white hover:text-black transition-colors duration-300 disabled:opacity-50">
                  {sending ? "Odesílání…" : "Odeslat poptávku"}
                </button>
                <p className="text-white/40 text-xs leading-relaxed" data-testid="inquiry-privacy-note">
                  Odesláním formuláře souhlasíte se zpracováním osobních údajů za účelem vyřízení poptávky. Více v{" "}
                  <Link to="/ochrana-osobnich-udaju" className="text-[#ff3b30] hover:underline">Zásadách ochrany osobních údajů</Link>.
                </p>
              </form>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex justify-between items-center">
    <span className="text-white/40 text-sm">{label}</span>
    <span className="text-white font-head font-medium flex items-center gap-2"><Check size={14} className="text-[#ff3b30]" /> {value}</span>
  </div>
);
